import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AmbassadorPage.module.css';

const IMG = '/static/images/ambassador';

const layoutAreas = `
  topbar
  main
  footer
`;

const INITIAL_FORM = {
  fullName: '',
  email: '',
  phone: '',
  role: '',
  dropzone: '',
  jumps: '',
  years: '',
  message: '',
};

// Fields rendered inside the 2-column grid (below full name, above message).
const GRID_FIELDS = [
  {
    name: 'email',
    label: 'Email Address',
    placeholder: 'you@gmail.com',
    type: 'email',
    required: true,
  },
  {
    name: 'phone',
    label: 'Phone Number',
    placeholder: '+00 00 000 00 00',
    type: 'tel',
    required: false,
  },
  {
    name: 'role',
    label: 'Role',
    placeholder: 'e.g. AFF-I, Coach..',
    type: 'text',
    required: true,
  },
  {
    name: 'dropzone',
    label: 'Main Dropzone',
    placeholder: 'Your DZ',
    type: 'text',
    required: true,
  },
  {
    name: 'jumps',
    label: 'Nr. of Jumps',
    placeholder: 'e.g. 1500',
    type: 'text',
    required: true,
  },
  {
    name: 'years',
    label: 'Years in the Sport',
    placeholder: 'e.g. 6',
    type: 'text',
    required: true,
  },
];

const TERMS = [
  {
    number: '1',
    title: 'One Payout Per Referred Jumper',
    text:
      'Each referred jumper qualifies for a single payout of EUR 50, triggered by their first completed purchase of EUR 1,000 or more on Avento. Only one payout is issued per individual, regardless of the number of purchases they make. Creating multiple accounts or referrals under different identities for the same person does not qualify and will be treated as abuse. Avento reserves the right to verify the identity of referred jumpers and withhold or reverse a payout in cases of suspected fraud or duplicate referrals.',
  },
  {
    number: '2',
    title: 'Self-Dealing Exclusion',
    text:
      "A payout is not issued when the referred jumper's qualifying purchase is made from a listing posted by the referring Ambassador. The referral and the transaction must involve independent parties. If a referred jumper's first purchase is from the referring Ambassador, his next purchase from an independent third party will qualify for the payout provided all other terms are met.",
  },
  {
    number: '3',
    title: 'Program Termination & Changes',
    text:
      'Avento reserves the right to modify or terminate the Ambassador Program at any time. If the program is terminated, jumpers who sign up via a referral link after the termination date will not qualify for a payout. Jumpers who signed up via a referral link before the termination date remain eligible for their first qualifying purchase, whenever that occurs. Avento will provide reasonable notice of any termination via email to all active Ambassadors before the change takes effect.',
  },
  {
    number: '4',
    title: 'Eligibility & Acceptance',
    text:
      "Participation in the program requires acceptance by Avento. Submitting an application does not guarantee admission. Avento may revoke Ambassador status at any time if these terms are breached or if an Ambassador's conduct is deemed contrary to the interests of the community or the platform. Referrals made prior to formal acceptance into the program do not qualify for payout.",
  },
  {
    number: '5',
    title: 'No Cap on Referrals',
    text:
      'There is no limit on the number of qualifying referrals an Ambassador can make. Each eligible referred jumper counts independently, provided all other conditions are met.',
  },
  {
    number: '6',
    title: 'Payout Process',
    text:
      'Payouts are processed manually by Avento after a qualifying transaction is confirmed and the inspection period has closed without dispute. Ambassadors are required to provide valid bank account details. Avento is not responsible for delays caused by incorrect or incomplete payment information. Payouts are issued in EUR.',
  },
];

function buildPageSchema(rootURL) {
  return {
    '@type': 'Service',
    name: 'Avento Ambassador Program',
    url: `${rootURL}/ambassador`,
    serviceType: 'Referral Program',
    description:
      'Referral program for skydiving instructors, coaches, and AFF-Is. Refer your students to Avento and earn EUR 50 per student when they complete their first purchase of EUR 1,000 or more.',
    provider: {
      '@type': 'Organization',
      '@id': `${rootURL}#organization`,
      name: 'Avento Gear GmbH',
      url: rootURL,
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    audience: {
      '@type': 'Audience',
      audienceType:
        'Skydiving instructors, AFF-Is, coaches, and trusted advisors in the skydiving community',
    },
    offers: {
      '@type': 'Offer',
      name: 'Ambassador Referral Reward',
      description:
        'Earn EUR 50 for every referred student who completes a first qualifying purchase of EUR 1,000 or more on Avento.',
      price: '50',
      priceCurrency: 'EUR',
      eligibleCustomerType:
        'Skydiving instructors, AFF-Is, coaches, and trusted advisors in the skydiving community',
    },
  };
}

const AmbassadorPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Avento Ambassador Program for Instructors and Coaches';
  const schemaDescription =
    'Refer your students to Avento and earn EUR 50 per first purchase. A program for instructors and coaches who help new skydivers get into the sport safely.';
  const pageSchema = useMemo(() => buildPageSchema(rootURL), [rootURL]);

  const [formValues, setFormValues] = useState(INITIAL_FORM);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [website, setWebsite] = useState('');
  const [formState, setFormState] = useState('idle');

  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleScrollToApply = e => {
    e.preventDefault();
    const el = document.getElementById('apply');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (website) return;

    setFormState('submitting');

    try {
      const response = await fetch('/api/ambassador', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formValues, acceptTerms }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      setFormState('success');
    } catch (err) {
      console.error('Ambassador form submission failed', err);
      setFormState('error');
    }
  };

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={pageSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="AmbassadorPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                <section className={css.pageWrapper}>
                  <div
                    className={css.hero}
                    style={{ '--heroBg': `url('${IMG}/sunset-angle.webp')` }}
                  >
                    <div className={css.heroInner}>
                      <div className={css.heroContent}>
                        <h1 className={css.heroTitle}>
                          Avento Ambassador Program
                        </h1>
                        <p className={css.heroSubtitle}>
                          A circle of instructors, coaches, and AFF-Is who
                          believe in what we’re building. Help your students
                          gear up the right way - and get rewarded when they
                          do.
                        </p>
                        <a
                          href="#apply"
                          className={css.primaryButton}
                          onClick={handleScrollToApply}
                        >
                          <span className={css.primaryButtonLabel}>Apply Now</span>
                          <img
                            src="/static/images/landing/hero-cta-arrow.svg"
                            alt=""
                            className={css.primaryButtonArrow}
                            width={28}
                            height={28}
                          />
                        </a>
                      </div>
                      <div className={css.heroImageWrap}>
                        <img
                          src={`${IMG}/sunset-angle.webp`}
                          alt="Tracking in Sunset"
                          className={css.heroImage}
                          loading="eager"
                          fetchpriority="high"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={css.section}>
                    <div className={css.container}>
                      <h2 className={css.sectionHeading}>How It Works</h2>
                      <div className={css.stepsGrid}>
                        <div className={css.stepCard}>
                          <div className={css.stepIconWrap}>
                            <img
                              src={`${IMG}/link.svg`}
                              alt="Link Icon"
                              className={css.stepIcon}
                              loading="lazy"
                            />
                          </div>
                          <h3 className={css.stepTitle}>
                            Get Your Referral Link
                          </h3>
                          <p className={css.stepText}>
                            Apply below and we’ll reply to you within 48 hours
                          </p>
                        </div>
                        <div className={css.stepCard}>
                          <div className={css.stepIconWrap}>
                            <img
                              src={`${IMG}/share-outline.svg`}
                              alt="Share Icon"
                              className={css.stepIcon}
                              loading="lazy"
                            />
                          </div>
                          <h3 className={css.stepTitle}>
                            Share It With Your Students
                          </h3>
                          <p className={css.stepText}>
                            Send it their way when they’re ready to start
                            shopping for their first rig.
                          </p>
                        </div>
                        <div className={css.stepCard}>
                          <div className={css.stepIconWrap}>
                            <img
                              src={`${IMG}/currency-dollar-outline.svg`}
                              alt="USD Icon"
                              className={css.stepIcon}
                              loading="lazy"
                            />
                          </div>
                          <h3 className={css.stepTitle}>
                            Earn EUR 50 Per Referred Jumper
                          </h3>
                          <p className={css.stepText}>
                            When they complete their first purchase of EUR
                            1,000 or more, we transfer EUR 50 straight to your
                            bank.
                          </p>
                        </div>
                      </div>
                      <p className={css.stepsNote}>
                        Payout is per referred jumper, not per purchase. No
                        cap, refer as many students as you want.
                      </p>
                    </div>
                  </div>

                  <div className={css.sectionDark}>
                    <div className={css.containerNarrow}>
                      <h2 className={css.sectionHeadingWhite}>
                        Why We’re Doing This
                      </h2>
                      <p className={css.bodyParaWhite}>
                        Skydiving is an expensive sport to get into. A first
                        rig is a major decision, and for most students, their
                        instructor’s word carries more weight than any
                        listing.
                      </p>
                      <p className={css.bodyParaWhite}>
                        Avento exists to make that process safer and more
                        transparent: a marketplace where gear is verified,
                        transactions are protected, and buyers have real
                        recourse if something goes wrong.
                      </p>
                      <p className={css.bodyParaWhite}>
                        If you’re already guiding students toward their first
                        purchase, this is our way of saying thank you.
                      </p>
                    </div>
                  </div>

                  <div className={css.section}>
                    <div className={css.container}>
                      <h2 className={css.sectionHeading}>Who Can Apply</h2>
                      <p className={css.bodyPara}>
                        The program is open to anyone who works in the sport
                        and regularly interacts with jumpers looking for
                        guidance on gear. That includes AFF instructors,
                        coaches, school operators, tandem instructors,
                        experienced licensed jumpers who advise others, and
                        anyone in a trusted advisory role in the community.
                      </p>
                      <p className={css.bodyPara}>
                        You can work full-time or part-time in skydiving. What
                        matters is that you are someone jumpers trust and come
                        to with questions about equipment.
                      </p>
                      <p className={css.highlight}>
                        <strong>
                          <em>
                            If you are regularly asked "what rig should I
                            get?" , you are exactly who we are looking for.
                          </em>
                        </strong>
                      </p>
                    </div>
                  </div>

                  <div className={css.sectionGrey} id="apply">
                    <div className={css.container}>
                      <h2 className={css.sectionHeading}>
                        Apply to the Avento Ambassador Program
                      </h2>
                      <p className={css.formSubtitle}>
                        Tell us a bit about yourself. We review every
                        application personally.
                      </p>
                      <div className={css.formLayout}>
                        {formState === 'success' ? (
                          <div className={css.formSuccess}>
                            <img
                              src="/static/images/contact/icon-check.png"
                              alt=""
                              className={css.formSuccessIcon}
                              width={50}
                              height={50}
                            />
                            <h3 className={css.formSuccessHeading}>Thank You!</h3>
                            <p className={css.formSuccessText}>
                              Your application has been submitted. We review every application personally and will be in touch shortly.
                            </p>
                          </div>
                        ) : (
                          <form
                            className={css.form}
                            onSubmit={handleSubmit}
                            noValidate
                          >
                            <div
                              aria-hidden="true"
                              className={css.honeypot}
                            >
                              <label htmlFor="website">Website</label>
                              <input
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={website}
                                onChange={e => setWebsite(e.target.value)}
                              />
                            </div>

                            <div className={css.formField}>
                              <label
                                className={css.formLabel}
                                htmlFor="fullName"
                              >
                                Full Name
                              </label>
                              <input
                                className={css.formInput}
                                type="text"
                                id="fullName"
                                name="fullName"
                                placeholder="Your name"
                                maxLength={256}
                                value={formValues.fullName}
                                onChange={handleChange}
                              />
                            </div>

                            <div className={css.formFieldsGrid}>
                              {GRID_FIELDS.map(field => (
                                <div
                                  key={field.name}
                                  className={css.formField}
                                >
                                  <label
                                    className={css.formLabel}
                                    htmlFor={field.name}
                                  >
                                    {field.label}
                                  </label>
                                  <input
                                    className={css.formInput}
                                    type={field.type}
                                    id={field.name}
                                    name={field.name}
                                    placeholder={field.placeholder}
                                    maxLength={256}
                                    required={field.required}
                                    value={formValues[field.name]}
                                    onChange={handleChange}
                                  />
                                </div>
                              ))}
                            </div>

                            <div className={css.formField}>
                              <label
                                className={css.formLabel}
                                htmlFor="message"
                              >
                                Message
                              </label>
                              <textarea
                                className={css.formTextarea}
                                id="message"
                                name="message"
                                placeholder="Tell us anything you want us to know."
                                maxLength={5000}
                                value={formValues.message}
                                onChange={handleChange}
                              />
                              <label className={css.formCheckboxField}>
                                <input
                                  className={css.formCheckbox}
                                  type="checkbox"
                                  id="acceptTerms"
                                  name="acceptTerms"
                                  required
                                  checked={acceptTerms}
                                  onChange={e =>
                                    setAcceptTerms(e.target.checked)
                                  }
                                />
                                <span className={css.formCheckboxLabel}>
                                  I accept the Ambassador Terms and Conditions
                                  (see below)
                                </span>
                              </label>
                            </div>

                            {formState === 'error' && (
                              <div className={css.formError}>
                                Oops! Something went wrong while submitting
                                the form. Please try again and reach out to us
                                if the problem persists.
                              </div>
                            )}

                            <button
                              type="submit"
                              className={css.primaryButton}
                              disabled={formState === 'submitting'}
                            >
                              {formState === 'submitting'
                                ? 'Please wait...'
                                : 'Submit'}
                            </button>
                          </form>
                        )}

                        <div className={css.sidebarCard}>
                          <h3 className={css.sidebarTitle}>What You Get</h3>
                          <ul className={css.sidebarList}>
                            <li>
                              EUR 50 for every jumper you refer who signs up
                              and makes their first purchase of EUR 1,000 or
                              more on Avento. Paid directly to your bank
                              account.
                            </li>
                            <li>
                              A way to support your students in finding safe
                              and affordable used gear through a platform
                              built for skydivers.
                            </li>
                            <li>
                              Personal Referral link, support from us anytime
                              :)
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${css.section} ${css.lastSection}`}>
                    <div className={css.container}>
                      <h2 className={css.sectionHeading}>
                        Ambassador Program - Terms & Conditions
                      </h2>
                      <div className={css.termsList}>
                        {TERMS.map(term => (
                          <div
                            key={term.number}
                            className={css.termsBlock}
                          >
                            <div className={css.termsNumber}>
                              {term.number}
                            </div>
                            <div className={css.termsContent}>
                              <h3 className={css.termsTitle}>{term.title}</h3>
                              <p className={css.termsText}>{term.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AmbassadorPage = connect(mapStateToProps)(AmbassadorPageComponent);

export default AmbassadorPage;
